/**
 * BundleBuilder-local RHF fork of CommonFields/TextFields.tsx — only the
 * subset of copy fields BundleBuilder actually renders (mirrors what the
 * pre-migration <TextFields /> call in BundleBuilderContentForm passed).
 */
import { useState } from "react";
import { BundleTypes } from "../../constants";
import styles from "../BundleBuilder.module.css";
import { InfoIcon } from "@shopify/polaris-icons";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import { useFormContext, useWatch } from "react-hook-form";
import { FormTextField } from "../../../../components/FormFields";
import useBundleMixpanelHook from "../../../../hooks/useBundleMixpanelHook";
import { Collapsible, Divider, FormLayout, Icon, Label, Tooltip } from "@shopify/polaris";

const TextFields = () => {
    const { control } = useFormContext();
    const secondaryButtonType = useWatch({ control, name: "secondary_button_type" });
    const [expandTextFields, setExpandTextFields] = useState(false);
    const { trackFormSections } = useBundleMixpanelHook();

    return (
        <>
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandTextFields((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Text Fields ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: BundleTypes.BUNDLE_BUILDER,
                        });
                        return !ps;
                    })
                }
            >
                Text Fields
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        transform: expandTextFields ? "rotate(90deg)" : "rotate(270deg)",
                    }}
                >
                    <MdOutlineArrowBackIosNew size={14} />
                </div>
            </div>
            <Collapsible
                open={expandTextFields}
                id="text-fields-collapsible"
                transition={{
                    duration: "500ms",
                    timingFunction: "ease-in-out",
                }}
                expandOnPrint
            >
                <FormLayout>
                    <div>
                        <div className={styles.customCardLabelWrapper} style={{ marginBottom: "5px" }}>
                            <Label id="">Product Card</Label>
                        </div>
                        <div className={styles.formRow}>
                            <FormTextField
                                name="buy_now_cta_text"
                                label="Product Card CTA"
                                placeholder="Buy Now"
                                maxLength={20}
                                showCharacterCount
                            />
                            <FormTextField
                                name="out_of_stock_text"
                                label="Out of stock"
                                placeholder="Out of stock"
                                maxLength={20}
                                showCharacterCount
                            />
                            <FormTextField
                                name="choose_option_text"
                                label="Choose Option Text"
                                placeholder="Choose Option"
                                maxLength={30}
                                showCharacterCount
                            />
                        </div>
                        <div className={styles.dividerStyle}>
                            <Divider />
                        </div>
                    </div>

                    <div>
                        <div className={styles.customCardLabelWrapper} style={{ marginBottom: "5px" }}>
                            <Label id="">Product Card Info Modal</Label>
                        </div>
                        <div className={styles.formRow}>
                            <FormTextField
                                name="show_more_text"
                                label="Show More Text"
                                placeholder="Show more"
                                maxLength={60}
                                showCharacterCount
                            />
                            <FormTextField
                                name="show_less_text"
                                label="Show Less Text"
                                placeholder="Show less"
                                maxLength={60}
                                showCharacterCount
                            />
                        </div>
                        <div className={styles.dividerStyle}>
                            <Divider />
                        </div>
                    </div>

                    <div>
                        <div className={styles.customCardLabelWrapper} style={{ marginBottom: "5px" }}>
                            <Label id="">Selected Products Bar</Label>
                        </div>
                        <div className={styles.formRow}>
                            <FormTextField
                                name="next_cta_text"
                                label="Next CTA"
                                placeholder="Next"
                                maxLength={10}
                                showCharacterCount
                            />
                            <FormTextField
                                name="previous_cta_text"
                                label="Previous CTA"
                                placeholder="Previous"
                                maxLength={10}
                                showCharacterCount
                            />
                        </div>
                        <div className={styles.dividerStyle}>
                            <Divider />
                        </div>
                    </div>

                    <div>
                        <div className={styles.customCardLabelWrapper} style={{ marginBottom: "5px" }}>
                            <Label id="">Review Page</Label>
                        </div>
                        <FormLayout>
                            <div className={styles.formRow}>
                                <FormTextField
                                    name="edit_cta_text"
                                    label="Edit Bundle CTA"
                                    placeholder="Edit Bundle"
                                    maxLength={20}
                                    showCharacterCount
                                    disabled={secondaryButtonType !== "edit_bundle"}
                                />
                                <FormTextField
                                    name="start_again_cta_text"
                                    label="Start Again CTA"
                                    placeholder="Start Again"
                                    maxLength={20}
                                    showCharacterCount
                                    disabled={secondaryButtonType !== "start_again"}
                                />
                                <FormTextField
                                    name="checkout_cta_text"
                                    label="Checkout CTA"
                                    placeholder="Checkout"
                                    maxLength={20}
                                    showCharacterCount
                                />
                                <FormTextField
                                    name="checkout_continue_shopping_cta_text"
                                    label="Continue Shopping CTA"
                                    placeholder="Continue Shopping"
                                    maxLength={30}
                                    showCharacterCount
                                />
                                <FormTextField
                                    name="checkout_view_cart_cta_text"
                                    label="View Cart CTA"
                                    placeholder="View Cart"
                                    maxLength={20}
                                    showCharacterCount
                                />
                            </div>
                            <div className={styles.formRow}>
                                <FormTextField
                                    name="bundle_ready_text"
                                    label="Bundle Ready Title"
                                    placeholder="title"
                                    maxLength={40}
                                    helpText="Use {title} for the bundle title"
                                    showCharacterCount
                                />
                                <FormTextField
                                    name="order_summary_title"
                                    label="Order Summary Title"
                                    placeholder="Order Summary"
                                    maxLength={20}
                                    showCharacterCount
                                />
                                <FormTextField
                                    name="order_summary_subtotal"
                                    label="Order Summary Subtotal"
                                    placeholder="subtotal"
                                    maxLength={20}
                                    showCharacterCount
                                />
                                <FormTextField
                                    name="order_summary_discount"
                                    label="Order Summary Discount"
                                    placeholder="discount"
                                    maxLength={20}
                                    showCharacterCount
                                />
                            </div>
                        </FormLayout>
                        <div className={styles.dividerStyle}>
                            <Divider />
                        </div>
                    </div>

                    <div>
                        <div className={styles.customCardLabelWrapper} style={{ marginBottom: "5px" }}>
                            <Label id="">
                                <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                                    Shopify Checkout
                                    <Tooltip
                                        width="wide"
                                        content='Shown on the bundle line in Shopify checkout: the label under each bundle item (e.g. "Box: 1") and the discount tag.'
                                    >
                                        <Icon source={InfoIcon} tone="subdued" />
                                    </Tooltip>
                                </div>
                            </Label>
                        </div>
                        <FormLayout>
                            <div className={styles.formRow}>
                                <FormTextField
                                    name="checkout_box_label"
                                    label="Box label"
                                    placeholder="Box"
                                    maxLength={20}
                                    showCharacterCount
                                    helpText='Shown as "Box: 1", "Box: 2"… (one number per bundle in the cart)'
                                />
                                <FormTextField
                                    name="checkout_discount_label"
                                    label="Discount label"
                                    placeholder="{discount} off"
                                    maxLength={40}
                                    showCharacterCount
                                    helpText="Use {discount} for the amount, e.g. 20% or 20"
                                />
                            </div>
                        </FormLayout>
                        <div className={styles.dividerStyle}>
                            <Divider />
                        </div>
                    </div>

                    <div>
                        <div className={styles.customCardLabelWrapper} style={{ marginBottom: "5px" }}>
                            <Label id="">Footer Text</Label>
                        </div>
                        <div className={styles.formRow}>
                            <FormTextField
                                name="footer_total_text"
                                label="Total"
                                placeholder="Total"
                                maxLength={10}
                                showCharacterCount
                            />
                            <FormTextField
                                name="footer_text"
                                label="Off"
                                placeholder="Off"
                                maxLength={10}
                                showCharacterCount
                            />
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <span
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        width: "100%",
                                        marginBottom: 5,
                                    }}
                                >
                                    <span>Bundle Item(s) Added</span>
                                    <Tooltip content="This text will be shown in the footer on mobile view">
                                        <Icon source={InfoIcon} />
                                    </Tooltip>
                                </span>
                                <FormTextField
                                    name="item_added_text"
                                    label="Bundle Item(s) Added"
                                    labelHidden
                                    placeholder="{itemCount} Item(s) Added"
                                    maxLength={40}
                                    showCharacterCount
                                />
                            </div>
                            <FormTextField
                                name="empty_card_footer_text"
                                label="Empty Card Footer Text"
                                placeholder="Add Product"
                                maxLength={30}
                                showCharacterCount
                            />
                        </div>
                        <div className={styles.dividerStyle}>
                            <Divider />
                        </div>
                    </div>

                    <div>
                        <div className={styles.customCardLabelWrapper}>
                            <Label id="">
                                <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                                    Free Text
                                    <Tooltip
                                        width="wide"
                                        content="This text will be shown when 100% discount is applied to the product."
                                    >
                                        <Icon source={InfoIcon} tone="subdued" />
                                    </Tooltip>
                                </div>
                            </Label>
                        </div>
                        <FormTextField
                            name="free_text"
                            label="Free Text"
                            labelHidden
                            placeholder="Free"
                            maxLength={35}
                            showCharacterCount
                        />
                    </div>
                </FormLayout>
            </Collapsible>
        </>
    );
};

export default TextFields;
